import { action, computed, makeObservable, observable } from "mobx"
import { PBAPI, PkgListData } from "react-pp-bundle"
import { CheckIsShowBundleData } from "react-pp-bundle"

import accountApi from "@/api/account"
import {
  AppPricingInterval,
  BundleAppPricingInterval,
  BundlePayType,
  CurrentSubscription,
  PayType,
  Plan,
  QuotaReminder,
} from "@/api/account/type.d"
import { PermissionRules } from "@/config/permission-rules"
import IUpgradeStore from "@/models/IUpgradeStore"
import { SubscriptionPaymentMethodEnum } from "@/models/IUserInfo"
import stores from "@/stores/index"
import { DateFormats, formatDate } from "@/utils/datetime"

export default class UpgradeStore implements IUpgradeStore {
  @observable upgradeModalActive: boolean = false
  @observable payType: PayType = AppPricingInterval.Every30_Days // 年费 or 月费
  @observable bundlePayType: BundlePayType = BundleAppPricingInterval.Every30_Days // Bundle套餐 年费 or 月费
  @observable btnText: string = "Upgrade now" // 在首次获取用户信息时，userInfo.ts --> setData() 方法就设置了这个文本
  @observable initialized: boolean = false
  @observable currentPlanInitialized: boolean = false
  @observable plans: Plan[] = []
  @observable currentPlan: CurrentSubscription = {
    "version": 2,
    "additional_quota_price": "0.05",
    "is_sub_shop": false,
    "quota_reminder": null,
    "spending_limit": "",
    "level": 0,
    "pricing_id": 0,
    "plan_name": "",
    "quota_total": 0,
    "quota_remain": 0,
    "additional_quota_used": 0,
    "interval": AppPricingInterval.Annual,
    "expiration_date": "",
    "billing_cycle_end_date": "",
    "trial_end_date": "",
    "next_plan": {
      "pricing_id": 0,
      "name": "",
      "quota": 0,
      "level": 0,
      "version": 4,
    },
    pricing_update: null,
  }
  @observable minPlanLevel: number = 2
  @observable activePlanLevel: number = 2
  @observable activeIndex: number[] = []

  @observable appType: "parcelpanel" | "bundle" = "parcelpanel"
  @observable showBundleChangePlanReminder: boolean = false

  // Bundle套餐推荐相关
  @observable perferPlanId?: number // 推荐购买的套餐Plan ID (也叫 Price id)，默认为当前套餐
  @observable preferKind?: number // 希望购买的套餐，Plan id 套餐ID，默认为当前套餐等级


  @observable showBannerData: CheckIsShowBundleData | null = null
  @action.bound
  setCheckIsShowBannerData(val: CheckIsShowBundleData) {
    this.showBannerData = val
  }


  constructor() {
    makeObservable(this)

    // 初始化推荐字段为当前套餐信息
    // this.perferPlanId = this.currentPlan.pricing_id
    // this.preferKind = this.currentPlan.level
  }


  @observable loading: boolean = false

  @action.bound
  setLoading(val: boolean) {
    this.loading = val
  }

  @observable bundleLoading: boolean = false

  @action.bound
  setBundleLoading(val: boolean) {
    this.bundleLoading = val
  }

  @observable bundleData: PkgListData | null = null
  @action.bound
  setBundleData(val: PkgListData) {
    this.bundleData = val
  }

  @action.bound
  async getBundleData() {
    this.setBundleLoading(true)
    try {
      const res = await PBAPI.getBundleList()

      if (res.code === 1000 && res.data) {
        this.setBundleData(res.data)
      }
      return res.data
    } finally {
      this.setBundleLoading(false)
    }
  }

  @action.bound
  async checkIsShowBundle() {
    this.setBundleLoading(true)
    try {
      const res2 = await PBAPI.checkIsShowBundle()

      if (res2.code === 1000 && res2.data) {
        this.setCheckIsShowBannerData(res2.data)
        if(res2.data.show_bundle){
          await this.getBundleData()
        }
      }
    } catch (error) {
      console.error(error)
    }
    this.setBundleLoading(false)
  }

  @action.bound
  setAppType(val: "parcelpanel" | "bundle") {
    this.appType = val
  }

  @action.bound
  setShowBundleChangePlanReminder(show: boolean) {
    this.showBundleChangePlanReminder = show
  }
  @computed get prevPlan() {
    const prev = this.plans.find(item => item.level === (this.activePlanLevel - 1))

    return prev as Plan
  }

  @computed get targetPlan() {
    const target = this.plans.find(item => item.level === this.activePlanLevel)

    return target as Plan
  }

  @action.bound
  handleCloseModal(): void {
    this.upgradeModalActive = false
  }

  @action.bound
  changeSpendingLimit(spending_limit:string): void {
    this.currentPlan.spending_limit = spending_limit
  }

  @action.bound
  showModal(permissionRule: PermissionRules): void {
    this.activeIndex = permissionRule.active_index
    this.showUpgrade(permissionRule.plan_member || 1)
  }

  /**
   * 清除套餐更新数据
   */
  @action.bound
  clearPricingUpdate(): void {
    this.currentPlan.pricing_update = null
  }

  /**
   * 展示最新付费引导
   * @param level
   */
  @action.bound
  showUpgrade(level: number): void {
    this.minPlanLevel = level // 最低level
    this.activePlanLevel = level
    this.upgradeModalActive = true
  }

  @action.bound
  setBtnText(btnText: string): void {
    this.btnText = btnText
  }

  @action.bound
  setPayType(type: PayType): void {
    this.payType = type
  }
  // 设置bundle套餐的支付年/月
  @action.bound
  setBundlePayType(type: BundlePayType): void {
    this.bundlePayType = type
  }

  @action.bound
  setPlans(data: Plan[]): void {
    this.plans = data
    this.initialized = true
  }

  @action.bound
  setCurrentPlan(data: CurrentSubscription, current_discount_interval?: AppPricingInterval): void {
    // 如果当前套餐发生变化，则更新用户信息接口
    if (data.pricing_id !== stores.userInfoSore.data.current_subscription.pricing_id){
      stores.userInfoSore.updateUserInfo()
    }
    data.billing_cycle_end_date = formatDate(data.billing_cycle_end_date, DateFormats.MONTH_DAY_YEAR)
    data.expiration_date = formatDate(data.expiration_date, DateFormats.MONTH_DAY_YEAR)
    data.trial_end_date = data.trial_end_date ? formatDate(data.trial_end_date, DateFormats.MONTH_DAY_YEAR) : null
    this.currentPlan = data
    if (data.interval === AppPricingInterval.Every30_Days) {
      // 如果是免费套餐，且当前折扣码是年费优惠. 老套餐也适用
      if ((data.level === 1 || data.level === 0) && current_discount_interval === AppPricingInterval.Annual) {
        this.setPayType(AppPricingInterval.Annual)
        this.setBundlePayType(BundleAppPricingInterval.Annual)
      } else {
        this.setPayType(AppPricingInterval.Every30_Days)
        this.setBundlePayType(BundleAppPricingInterval.Every30_Days)
      }
    } else if (data.interval === AppPricingInterval.Annual) {
      this.setPayType(AppPricingInterval.Annual)
      this.setBundlePayType(BundleAppPricingInterval.Annual)
    }

    if (stores.userInfoSore.data.subscription_payment_method === SubscriptionPaymentMethodEnum.PARCELPANEL){
      // 自动设置推荐字段为当前套餐信息
      // this.setPerferPlanId(data.pricing_id)
      // this.setPreferKind(data.level)
    }

    this.currentPlanInitialized = true
  }

  @action.bound
  setMinPlanLevel(level: number): void {
    this.minPlanLevel = level
  }

  @action.bound
  setActivePlanLevel(level: number): void {
    if (this.activePlanLevel !== level) {
      this.activePlanLevel = level
    }
  }

  /** 进行中的 plans 请求，并发调用时复用，避免重复打接口 */
  private plansRequest: Promise<void> | null = null

  @action.bound
  updatePlans() {
    if (this.plansRequest) {
      return this.plansRequest
    }

    this.plansRequest = accountApi.getPlanList()
      .then(({ data: { plans } }) => {
        this.setPlans(plans)
      })
      .finally(() => {
        this.plansRequest = null
      })

    return this.plansRequest
  }
  @action.bound
  updateQuotaReminder(quota_reminder: QuotaReminder) {
    this.currentPlan.quota_reminder = quota_reminder
  }

  // Bundle套餐推荐相关的setter方法
  @action.bound
  setPerferPlanId(planId?: number): void {
    this.perferPlanId = planId
  }

  @action.bound
  setPreferKind(kind?: number): void {
    this.preferKind = kind
  }
}
